import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { type Note, fmtDate, fmtNoteStamp, localToday, normalize } from '@quadro/domain';
import { confirmAction } from '../../components/Confirm';
import { hud } from '../../components/Hud';
import { Icon } from '../../components/Icon';
import * as api from '../../data/api';
import { keys, useNotes } from '../../data/queries';
import { Page } from '../../layout/Page';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';

const firstLine = (body: string) => body.split('\n').find(x => x.trim()) || 'Nenhum texto adicional';

/** Notas pessoais (só o dono vê, garantido por RLS). */
export function NotesPage() {
  const ws = useWorkspace();
  const qc = useQueryClient();
  const notes = useNotes().data ?? [];
  const { notesFilter, noteSearch, selectedNote, set } = useUI();
  const q = normalize(noteSearch.trim());
  const list = notes.filter(n => (notesFilter !== 'pinned' || n.pinned) && (!q || normalize(`${n.title} ${n.body}`).includes(q))).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const current = notes.find(n => n.id === selectedNote) ?? list[0] ?? null;
  const pending = useRef<{ id: string; title: string; body: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  /** Criação em andamento: salvar ou apagar a nota nova espera por ela. */
  const creating = useRef<Promise<unknown>>(Promise.resolve());

  const flush = async () => {
    clearTimeout(timer.current);
    await creating.current;
    const p = pending.current; pending.current = null;
    if (p) await api.updateNote(p.id, { title: p.title, body: p.body }).catch(e => hud((e as Error).message));
  };
  const patchCache = (id: string, p: Partial<Note>) => qc.setQueryData<Note[]>(keys.notes, l => l?.map(n => (n.id === id ? { ...n, ...p } : n)));
  /** Ao sair de uma nota vazia, ela é descartada. */
  const leave = async () => {
    await flush();
    const n = qc.getQueryData<Note[]>(keys.notes)?.find(x => x.id === useUI.getState().selectedNote);
    if (n && !n.title.trim() && !n.body.trim()) { qc.setQueryData<Note[]>(keys.notes, l => l?.filter(x => x.id !== n.id)); await api.deleteNote(n.id).catch(() => undefined); }
  };
  useEffect(() => { window.addEventListener('beforeunload', flush); return () => { window.removeEventListener('beforeunload', flush); void leave(); }; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (n: Note, p: { title?: string; body?: string }) => {
    const next = { id: n.id, title: p.title ?? n.title, body: p.body ?? n.body };
    pending.current = next;
    patchCache(n.id, { ...p, updatedAt: new Date().toISOString() });
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  };
  /** A nota aparece na hora (id gerado aqui), para o que for digitado não cair na nota anterior. */
  const create = async () => {
    await leave();
    const n: Note = { id: crypto.randomUUID(), title: '', body: '', pinned: notesFilter === 'pinned', updatedAt: new Date().toISOString() };
    qc.setQueryData<Note[]>(keys.notes, l => [n, ...(l ?? [])]);
    set({ selectedNote: n.id, noteSearch: '' });
    setTimeout(() => document.getElementById('edTitulo')?.focus(), 30);
    creating.current = api.createNote(n.pinned, n.id).catch(e => {
      pending.current = null;
      qc.setQueryData<Note[]>(keys.notes, l => l?.filter(x => x.id !== n.id));
      hud((e as Error).message);
    });
  };

  const item = (n: Note) => (
    <button key={n.id} className="nitem" aria-current={n.id === current?.id} onClick={async () => { if (n.id !== current?.id) { await leave(); set({ selectedNote: n.id }); } }}>
      <div className="ni-t">{n.title || 'Nova nota'}</div>
      <div className="ni-s"><b>{fmtNoteStamp(n.updatedAt, localToday())}</b>{firstLine(n.body)}</div>
    </button>
  );
  const pinned = list.filter(n => n.pinned), rest = list.filter(n => !n.pinned);

  return (
    <Page toolbar={<>
      <div className="tb-title"><h1>{notesFilter === 'pinned' ? 'Notas fixadas' : 'Notas'}</h1><span className="sub">só você vê</span></div>
      <div className="tb-space" />
      <div className="tools">
        <button className="tb-btn" aria-label="Nova nota" title="Nova nota" disabled={!ws.canWrite} onClick={create}><Icon name="compose" /></button>
        <button className={`tb-btn ${current?.pinned ? 'on' : ''}`} disabled={!current || !ws.canWrite} aria-label={current?.pinned ? 'Desafixar nota' : 'Fixar nota'}
          onClick={async () => { if (!current) return; await flush(); patchCache(current.id, { pinned: !current.pinned }); await api.updateNote(current.id, { pinned: !current.pinned }); hud(current.pinned ? 'Nota desafixada' : 'Nota fixada'); }}>
          <Icon name={current?.pinned ? 'pinOn' : 'pin'} />
        </button>
        <button className="tb-btn" disabled={!current} aria-label="Apagar nota"
          onClick={() => current && confirmAction({ title: 'Apagar esta nota?', text: `“${current.title || 'Nova nota'}” será apagada.`, action: 'Apagar', onConfirm: async () => {
            clearTimeout(timer.current); pending.current = null;
            qc.setQueryData<Note[]>(keys.notes, l => l?.filter(x => x.id !== current.id));
            set({ selectedNote: null });
            await creating.current;
            await api.deleteNote(current.id).catch(e => hud((e as Error).message));
          } })}><Icon name="trash" /></button>
        <label className="search"><Icon name="search" /><input type="search" placeholder="Buscar" aria-label="Buscar notas" value={noteSearch} onChange={e => set({ noteSearch: e.target.value })} /></label>
      </div>
    </>}>
      <div className="notes">
        <div className="nlist">
          {!list.length ? <p className="empty" style={{ padding: '40px 10px' }}>{noteSearch ? 'Nenhuma nota encontrada.' : 'Nenhuma nota.'}</p>
            : notesFilter === 'pinned' ? pinned.map(item)
            : <>{pinned.length > 0 && <><div className="nsec">Fixadas</div>{pinned.map(item)}</>}{rest.length > 0 && <>{pinned.length > 0 && <div className="nsec">Notas</div>}{rest.map(item)}</>}</>}
        </div>
        <div className="editor">
          {current ? <Editor key={current.id} note={current} readOnly={!ws.canWrite} onEdit={p => edit(current, p)} onBlur={flush} /> : <p className="empty">Nenhuma nota selecionada.</p>}
        </div>
      </div>
    </Page>
  );
}

function Editor({ note, readOnly, onEdit, onBlur }: { note: Note; readOnly: boolean; onEdit: (p: { title?: string; body?: string }) => void; onBlur: () => void }) {
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const d = new Date(note.updatedAt);
  return (
    <div className="ed-inner">
      <p className="ed-date">{fmtDate(d, { day: 'numeric', month: 'long', year: 'numeric' })} às {String(d.getHours()).padStart(2, '0')}:{String(d.getMinutes()).padStart(2, '0')}</p>
      <input className="ed-title" id="edTitulo" placeholder="Título" aria-label="Título da nota" maxLength={120} disabled={readOnly} value={title}
        onChange={e => { setTitle(e.target.value); onEdit({ title: e.target.value }); }} onBlur={onBlur}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); bodyRef.current?.focus(); bodyRef.current?.setSelectionRange(0, 0); } }} />
      <textarea className="ed-body" ref={bodyRef} placeholder="Comece a escrever" aria-label="Texto da nota" disabled={readOnly} value={body}
        onChange={e => { setBody(e.target.value); onEdit({ body: e.target.value }); }} onBlur={onBlur} />
    </div>
  );
}
